import { z } from 'zod';
import {
  documentOptionalSchema,
  selectionRangeTableOptionalSchema,
  selectionRangeTableSchema,
  USAAddressDefaultValues,
  USAAddressOptionalSchema,
  USAAddressSchema,
} from '../../../constants/sharedSchemas';

export const interviewSchema = {
  required: z.object({
    // Interview Details
    interviewDetails: z.object({
      interviewLength: z
        .string()
        .refine((val) => val !== '', {
          message: 'Interview Length is required',
        })
        .refine((val) => val === '' || Number(val) > 45, {
          message: 'Interview must be at least 45 minutes long',
        }),
      interviewDate: z.date({ required_error: 'Date of Interview is required' }),
      interviewerName: z.string().min(1, 'Name of Interviewer is required'),
      gradeLevel: z.string().min(1, 'Student grade level is required'),
      recommendedGrade: z.string().min(1, 'Recommended grade is required'),
      gpa: z.string().min(1, 'GPA is required'),
    }),

    // Special requests
    specialRequests: z.object({
      stateChoice1: z.string().min(1, 'State Choice is required'),
      stateChoice2: z.string().min(1, 'State Choice is required'),
      stateChoice3: z.string().min(1, 'State Choice is required'),
      requests: z.array(z.string()).optional(),
    }),

    // Direct Placement
    directPlacement: z.object({
      studentRequestingPlacement: z.boolean(),
      requestedHFName: z.string().min(1, 'Requested Host Family Name is required'),
      requestedHFPhone: z.string().min(1, 'Requested Host Family Phone is required'),
      requestedHFAddress: USAAddressSchema,
      requestedSchoolName: z.string().min(1, 'Requested School Name is required'),
      requestedSchoolPhone: z.string().min(1, 'Requested School Phone is required'),
      requestedSchoolAddress: USAAddressSchema,
    }),

    // Student evaluation
    studentEvaluation: z.object({
      studentCharacter: z.string().min(1, "Student's Character is required"),
      interviewerComments: z.string().min(1, "Interviewer's Comments is required"),
      academicInterestDegree: z.string().min(1, 'Degree of academic interest is required'),
      personalityRanges: selectionRangeTableSchema,
    }),

    // English comprehension score
    englishScore: z.object({
      englishLevel: z.string().min(1, 'English Comprehension Score is required'),
      successPotential: z.string().min(1, 'Success potential is required'),
      // TODO: change type once upload endpoints are integrated
      interviewReport: documentOptionalSchema,
    }),
  }),
  optional: z.object({
    // Interview Details
    interviewDetails: z.object({
      interviewLength: z.string().optional(),
      interviewDate: z.date().optional(),
      interviewerName: z.string().optional(),
      gradeLevel: z.string().optional(),
      recommendedGrade: z.string().optional(),
      gpa: z.string().optional(),
    }),

    // Special requests
    specialRequests: z.object({
      stateChoice1: z.string().optional(),
      stateChoice2: z.string().optional(),
      stateChoice3: z.string().optional(),
      requests: z.array(z.string()).optional(),
    }),

    // Direct Placement
    directPlacement: z.object({
      studentRequestingPlacement: z.boolean(),
      requestedHFName: z.string().optional(),
      requestedHFPhone: z.string().optional(),
      requestedHFAddress: USAAddressOptionalSchema,
      requestedSchoolName: z.string().optional(),
      requestedSchoolPhone: z.string().optional(),
      requestedSchoolAddress: USAAddressOptionalSchema,
    }),

    // Student evaluation
    studentEvaluation: z.object({
      studentCharacter: z.string().optional(),
      interviewerComments: z.string().optional(),
      academicInterestDegree: z.string().optional(),
      personalityRanges: selectionRangeTableOptionalSchema,
    }),

    // English comprehension score
    englishScore: z.object({
      englishLevel: z.string().optional(),
      successPotential: z.string().optional(),
      // TODO: change type once upload endpoints are integrated
      interviewReport: documentOptionalSchema,
    }),
  }),
};

export type InterviewFormValues = z.infer<(typeof interviewSchema)['required']> | z.infer<(typeof interviewSchema)['optional']>;

export const defaultValues: InterviewFormValues = {
  // Interview Details
  interviewDetails: {
    interviewLength: '',
    interviewDate: undefined,
    interviewerName: '',
    gradeLevel: '',
    recommendedGrade: '',
    gpa: '',
  },

  // Special requests
  specialRequests: {
    stateChoice1: '',
    stateChoice2: '',
    stateChoice3: '',
    requests: [],
  },

  // Direct Placement
  directPlacement: {
    studentRequestingPlacement: false,
    requestedHFName: '',
    requestedHFPhone: '',
    requestedHFAddress: USAAddressDefaultValues,
    requestedSchoolName: '',
    requestedSchoolPhone: '',
    requestedSchoolAddress: USAAddressDefaultValues,
  },

  // Student evaluation
  studentEvaluation: {
    studentCharacter: '',
    interviewerComments: '',
    academicInterestDegree: '',
    personalityRanges: {
      opinionatedOpenMinded: '',
      nervousCalm: '',
      shyAssertive: '',
      introspectiveOutgoing: '',
      rigidFlexible: '',
      protectedIndependent: '',
      passiveTalkative: '',
    },
  },

  // English comprehension score
  englishScore: {
    englishLevel: '',
    successPotential: '',
    // TODO: change type once upload endpoints are integrated
    interviewReport: [],
  },
};
